/*
 * Vajihi Labs - Email Signature Builder
 * App bootstrap: Templates/Customize/Apply tab switching, the Settings and
 * Expand overlays, and the standalone-preview banner. Runs its own UI setup
 * on DOMContentLoaded regardless of Office.js, then separately waits on
 * Office.onReady if the API is present at all — mirrors the previous
 * build's approach so the app still opens and works as a design-time
 * preview outside Outlook.
 */

/* global document, Office */

import { activateApply, getApplyPreviewHtml, initApply } from "./apply";
import { activateCustomize, getCustomizePreviewHtml, initCustomize, refreshCustomize } from "./customize";
import { activateTemplates, initTemplates } from "./templates";
import { getModel, updateModel } from "./lib/appState";
import { getUserProfile, isStandalonePreview } from "./lib/outlookIntegration";
import { sanitizeLogoUrl } from "./lib/validation";

type ViewId = "templates" | "customize" | "apply";
let activeView: ViewId = "templates";

function byId<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function setText(id: string, text: string, isError = false): void {
  const el = byId<HTMLElement>(id);
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("status--error", isError);
}

function setActiveView(view: ViewId): void {
  activeView = view;
  const tabs: Record<ViewId, string> = { templates: "tab-templates", customize: "tab-customize", apply: "tab-apply" };
  const panels: Record<ViewId, string> = { templates: "panel-templates", customize: "panel-customize", apply: "panel-apply" };
  (Object.keys(tabs) as ViewId[]).forEach((v) => {
    const tab = byId<HTMLButtonElement>(tabs[v]);
    const panel = byId<HTMLElement>(panels[v]);
    const active = v === view;
    tab?.setAttribute("aria-selected", String(active));
    if (panel) panel.hidden = !active;
  });

  if (view === "templates") activateTemplates();
  else if (view === "customize") activateCustomize(getUserProfile);
  else activateApply();
}

function initTabs(): void {
  byId<HTMLButtonElement>("tab-templates")?.addEventListener("click", () => setActiveView("templates"));
  byId<HTMLButtonElement>("tab-customize")?.addEventListener("click", () => setActiveView("customize"));
  byId<HTMLButtonElement>("tab-apply")?.addEventListener("click", () => setActiveView("apply"));
  byId<HTMLButtonElement>("change-theme-link")?.addEventListener("click", () => setActiveView("templates"));
}

function updateStandaloneBanner(): void {
  const banner = byId<HTMLElement>("standalone-banner");
  if (banner) banner.hidden = !isStandalonePreview();
}

// --------------------------------------------------------------------------
// Settings overlay
// --------------------------------------------------------------------------
function refreshActiveViewAfterSettingsChange(): void {
  if (activeView === "customize") refreshCustomize();
  else if (activeView === "apply") activateApply();
}

function openSettings(): void {
  const model = getModel();
  const lightInput = byId<HTMLInputElement>("settings-logo-light-url");
  const darkInput = byId<HTMLInputElement>("settings-logo-dark-url");
  if (lightInput) lightInput.value = model.logos.lightSurfaceUrl;
  if (darkInput) darkInput.value = model.logos.darkSurfaceUrl;
  setText("settings-logo-status", "");
  const overlay = byId<HTMLElement>("settings-overlay");
  if (overlay) overlay.hidden = false;
  byId<HTMLButtonElement>("settings-close-btn")?.focus();
}

function closeSettings(): void {
  const overlay = byId<HTMLElement>("settings-overlay");
  if (overlay) overlay.hidden = true;
  byId<HTMLButtonElement>("settings-btn")?.focus();
}

function initSettings(): void {
  byId<HTMLButtonElement>("settings-btn")?.addEventListener("click", openSettings);
  byId<HTMLButtonElement>("settings-close-btn")?.addEventListener("click", closeSettings);
  byId<HTMLElement>("settings-overlay")?.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeSettings();
  });
  byId<HTMLElement>("settings-overlay")?.addEventListener("click", (e) => {
    if (e.target === byId("settings-overlay")) closeSettings();
  });

  const validateAndStore = (input: HTMLInputElement, field: "lightSurfaceUrl" | "darkSurfaceUrl") => {
    const value = input.value.trim();
    if (value && !sanitizeLogoUrl(value)) {
      setText("settings-logo-status", "Enter a valid https:// (or cid:) address.", true);
      return;
    }
    setText("settings-logo-status", "");
    updateModel((m) => {
      m.logos[field] = value;
    });
    refreshActiveViewAfterSettingsChange();
  };

  byId<HTMLInputElement>("settings-logo-light-url")?.addEventListener("input", (e) => validateAndStore(e.target as HTMLInputElement, "lightSurfaceUrl"));
  byId<HTMLInputElement>("settings-logo-dark-url")?.addEventListener("input", (e) => validateAndStore(e.target as HTMLInputElement, "darkSurfaceUrl"));
}

// --------------------------------------------------------------------------
// Expand overlay
// --------------------------------------------------------------------------
let expandReturnFocusId = "";

function openExpand(html: string, heading: string, returnFocusId: string): void {
  expandReturnFocusId = returnFocusId;
  const headingEl = byId<HTMLElement>("expand-heading");
  const content = byId<HTMLElement>("expand-content");
  if (headingEl) headingEl.textContent = heading;
  if (content) content.innerHTML = html;
  const overlay = byId<HTMLElement>("expand-overlay");
  if (overlay) overlay.hidden = false;
  byId<HTMLButtonElement>("expand-close-btn")?.focus();
}

function closeExpand(): void {
  const overlay = byId<HTMLElement>("expand-overlay");
  if (overlay) overlay.hidden = true;
  if (expandReturnFocusId) byId<HTMLButtonElement>(expandReturnFocusId)?.focus();
}

function initExpand(): void {
  byId<HTMLButtonElement>("expand-close-btn")?.addEventListener("click", closeExpand);
  byId<HTMLElement>("expand-overlay")?.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeExpand();
  });
  byId<HTMLButtonElement>("customize-expand-btn")?.addEventListener("click", () => {
    openExpand(getCustomizePreviewHtml(), "Signature preview", "customize-expand-btn");
  });
}

// --------------------------------------------------------------------------
// Bootstrap
// --------------------------------------------------------------------------
function initApp(): void {
  updateStandaloneBanner();
  initTabs();
  initSettings();
  initExpand();
  initTemplates(() => setActiveView("customize"));
  initCustomize(() => setActiveView("apply"));
  initApply({
    onEditSignature: () => setActiveView("customize"),
    onEditPerson: () => {
      setActiveView("customize");
      const d = byId<HTMLDetailsElement>("personal-details-disclosure");
      if (d) d.open = true;
      byId<HTMLInputElement>("person-name-input")?.focus();
    },
    onEditTheme: () => setActiveView("templates"),
    onEditLogoLinks: () => {
      setActiveView("customize");
      const d = byId<HTMLDetailsElement>("brand-logo-disclosure");
      if (d) d.open = true;
      byId<HTMLElement>("logo-empty-state")?.focus();
    },
    onNeedHelp: openSettings,
    onExpand: () => {
      openExpand(getApplyPreviewHtml(), "Signature preview", "apply-expand-btn");
    },
  });

  setActiveView("templates");
}

document.addEventListener("DOMContentLoaded", () => {
  initApp();
});

if (typeof Office !== "undefined") {
  Office.onReady(() => {
    updateStandaloneBanner();
  });
}
